﻿import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Input, InputNumber, Modal } from 'antd'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton, DemoBadge, TableLoadingRow } from '@/widgets/demo/ListPrimitives'
import { OfficeReviewStatusTag, useToast } from '@/widgets/common'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { paymentJobsApi } from '@/services/paymentJobs/api'
import type {
  PaymentAdjustmentAttachmentInfo,
  PaymentJobRecipientResponse,
  PaymentJobResponse,
} from '@/services/paymentJobs/types'
import { getErrorMessage, isHandledAuthError } from '@/services/client'
import { CASE_STATUS_CODE_MAP, type CaseStatus } from '@/constants/enums'
import useSearchPersistence from '@/hooks/useSearchPersistence'
import { useOfficeReviewStatuses, usePermission } from '@/hooks'
import { downloadBlob } from '@/utils/download'

function parsePositiveInt(value: string | null): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

const EMPTY_DISPLAY = '-'

const ADJUSTABLE_CASE_STATUSES = new Set(['NOTICE_PRINTED', 'NOTICE_DOWNLOADED'])

function formatYen(value: number | null | undefined): string {
  return value == null || Number.isNaN(Number(value)) ? EMPTY_DISPLAY : `${Number(value).toLocaleString()}円`
}

function formatSignedYen(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return EMPTY_DISPLAY
  const numeric = Number(value)
  return `${numeric > 0 ? '+' : ''}${numeric.toLocaleString()}円`
}

function formatAdjustedAt(value: string | null | undefined): string {
  if (!value) return EMPTY_DISPLAY
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ja-JP')
}

function formatAttachmentDateTime(value: string | null | undefined): string {
  if (!value) return EMPTY_DISPLAY
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return EMPTY_DISPLAY
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`
}

// 支給者一覧（クライアント側の絞り込み）の最後に適用した検索条件を localStorage に保持する
// （顧客要望：「検索条件は最後に選択した結果を保持する」）。ページング・ソートは無い画面のため条件のみ。
// 他画面と同様にフラットなキーを用い、支給者詳細から戻った際に直前の絞り込みを復元する。
interface PaymentJobDetailSearch {
  identId: string
  name: string
  householdNo: string
  /** 事務局確認用ステータス絞り込み（No.25）。ALL / '0'=未設定 / master.id。 */
  officeReviewUi: string
}

// 事務局確認用ステータス絞り込み。この画面はリスト明細をクライアント側で絞るため、
// 受給者検索と同じ選択値の流儀（ALL / '0'=未設定 / master.id）だけ合わせる。
const OFFICE_REVIEW_ALL_VALUE = 'ALL'
const OFFICE_REVIEW_UNSET_VALUE = '0'

const DEFAULT_SEARCH: PaymentJobDetailSearch = {
  identId: '',
  name: '',
  householdNo: '',
  officeReviewUi: OFFICE_REVIEW_ALL_VALUE,
}

const PaymentJobDetail = () => {
  const navigate = useAppNavigate()
  const toast = useToast()
  const { jobId: jobIdParam } = useParams<{ jobId?: string }>()
  const [job, setJob] = useState<PaymentJobResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [savedSearch, setSavedSearch] = useSearchPersistence<PaymentJobDetailSearch>('payment-job-detail', DEFAULT_SEARCH)
  const [qIdentId, setQIdentId] = useState(savedSearch.identId)
  const [qName, setQName] = useState(savedSearch.name)
  const [qHouseholdNo, setQHouseholdNo] = useState(savedSearch.householdNo)
  // 保存値が無い（旧バージョンの保存内容）場合は「全て」。
  const [qOfficeReview, setQOfficeReview] = useState(savedSearch.officeReviewUi || OFFICE_REVIEW_ALL_VALUE)
  const { statuses: officeReviewStatuses } = useOfficeReviewStatuses()
  const { hasPermission } = usePermission()
  const canEditPayment = hasPermission('PAYMENT_LIST_EDIT')
  const canManageAdjustmentAttachments = hasPermission('PAYMENT_NOTICE_DOWNLOAD')
  const [editingRow, setEditingRow] = useState<PaymentJobRecipientResponse | null>(null)
  const [detailRow, setDetailRow] = useState<PaymentJobRecipientResponse | null>(null)
  const [adjustmentAmount, setAdjustmentAmount] = useState<number | null>(null)
  const [adjustmentReason, setAdjustmentReason] = useState('')
  const [savingAdjustment, setSavingAdjustment] = useState(false)
  const [attachmentProcessingLineId, setAttachmentProcessingLineId] = useState<number | null>(null)
  const [attachmentListRow, setAttachmentListRow] = useState<PaymentJobRecipientResponse | null>(null)
  const [attachmentFiles, setAttachmentFiles] = useState<PaymentAdjustmentAttachmentInfo[]>([])
  const [attachmentListLoading, setAttachmentListLoading] = useState(false)
  const [applied, setApplied] = useState<PaymentJobDetailSearch>({
    identId: savedSearch.identId,
    name: savedSearch.name,
    householdNo: savedSearch.householdNo,
    officeReviewUi: savedSearch.officeReviewUi || OFFICE_REVIEW_ALL_VALUE,
  })

  const effectiveJobId = parsePositiveInt(jobIdParam ?? null)

  const loadJob = useCallback(async () => {
    if (!effectiveJobId) return

    setLoading(true)
    try {
      const { data } = await paymentJobsApi.getById(effectiveJobId)
      setJob(data)
    } catch (error) {
      if (isHandledAuthError(error)) return
      navigate('payment-job')
    } finally {
      setLoading(false)
    }
  }, [effectiveJobId, navigate])

  useEffect(() => {
    if (!effectiveJobId) {
      Modal.error({ content: '表示する支払リストが選択されていません。' })
      navigate('payment-job')
      return
    }
    // 支払リスト詳細を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadJob()
  }, [effectiveJobId, loadJob, navigate])

  const filteredRows = useMemo(() => {
    const rows = job?.recipients ?? []
    return rows.filter((row) => {
      if (applied.identId && !String(row.identId ?? '').toLowerCase().includes(applied.identId.toLowerCase())) {
        return false
      }
      if (applied.name && !String(row.name ?? '').includes(applied.name)) {
        return false
      }
      if (applied.householdNo && !String(row.householdNo ?? '').includes(applied.householdNo)) {
        return false
      }
      if (applied.officeReviewUi !== OFFICE_REVIEW_ALL_VALUE) {
        // '0'（未設定）は null/undefined の行だけを残す。
        const statusId = row.officeReviewStatusId ?? null
        const wanted = Number(applied.officeReviewUi)
        if (applied.officeReviewUi === OFFICE_REVIEW_UNSET_VALUE ? statusId != null : statusId !== wanted) {
          return false
        }
      }
      return true
    })
  }, [applied, job?.recipients])

  const runSearch = () => {
    const nextApplied: PaymentJobDetailSearch = {
      identId: qIdentId.trim(),
      name: qName.trim(),
      householdNo: qHouseholdNo.trim(),
      officeReviewUi: qOfficeReview,
    }
    setApplied(nextApplied)
    setSavedSearch(nextApplied)
    void loadJob()
  }

  const runClear = () => {
    setQIdentId('')
    setQName('')
    setQHouseholdNo('')
    setQOfficeReview(OFFICE_REVIEW_ALL_VALUE)
    const clearedApplied: PaymentJobDetailSearch = {
      identId: '',
      name: '',
      householdNo: '',
      officeReviewUi: OFFICE_REVIEW_ALL_VALUE,
    }
    setApplied(clearedApplied)
    setSavedSearch(clearedApplied)
  }

  const openAdjustment = (row: PaymentJobRecipientResponse) => {
    setEditingRow(row)
    setAdjustmentAmount(Number(row.adjustmentAmount ?? 0))
    setAdjustmentReason('')
  }

  const closeAdjustment = () => {
    if (savingAdjustment) return
    setEditingRow(null)
    setAdjustmentAmount(null)
    setAdjustmentReason('')
  }

  const saveAdjustment = async () => {
    if (!effectiveJobId || !editingRow || adjustmentAmount == null || !Number.isInteger(adjustmentAmount)) {
      toast.warning('調整額は整数の円単位で入力してください。')
      return
    }
    const reason = adjustmentReason.trim()
    if (!reason) {
      toast.warning('修正理由を入力してください。')
      return
    }
    if (reason.length > 128) {
      toast.warning('修正理由は128文字以内で入力してください。')
      return
    }
    const calculated = Number(editingRow.calculatedAmount ?? editingRow.amount ?? 0)
    const finalAmount = calculated + adjustmentAmount
    if (finalAmount < 0) {
      toast.warning('振込金額が0円未満になる調整はできません。')
      return
    }
    if (finalAmount > 9_999_999_999) {
      toast.warning('振込金額は9,999,999,999円以下にしてください。')
      return
    }

    setSavingAdjustment(true)
    try {
      await paymentJobsApi.adjustAmount(effectiveJobId, editingRow.id, {
        adjustmentAmount,
        reason,
        version: editingRow.version,
      })
      await loadJob()
      setEditingRow(null)
      setAdjustmentAmount(null)
      setAdjustmentReason('')
      toast.success('振込金額を更新しました。')
    } catch (error) {
      if (!isHandledAuthError(error)) {
        Modal.error({ content: getErrorMessage(error, '振込金額を更新できませんでした。') })
        await loadJob()
      }
    } finally {
      setSavingAdjustment(false)
    }
  }

  const editingCalculatedAmount = Number(editingRow?.calculatedAmount ?? editingRow?.amount ?? 0)
  const editingFinalAmount = adjustmentAmount == null ? editingCalculatedAmount : editingCalculatedAmount + adjustmentAmount

  const fetchPaymentAdjustmentAttachments = async (
    row: PaymentJobRecipientResponse,
    showModal: boolean,
  ) => {
    if (!effectiveJobId) return
    if (showModal) {
      setAttachmentListRow(row)
      setAttachmentFiles([])
      setAttachmentListLoading(true)
    }
    try {
      const { data } = await paymentJobsApi.listPaymentAdjustmentAttachments(effectiveJobId, row.id)
      if (showModal || attachmentListRow?.id === row.id) {
        setAttachmentFiles(data)
      }
    } catch (error) {
      if (!isHandledAuthError(error)) {
        Modal.error({ title: 'システムエラー', content: '添付ファイル一覧を取得できませんでした。' })
      }
      if (showModal) setAttachmentListRow(null)
    } finally {
      if (showModal) setAttachmentListLoading(false)
    }
  }

  const uploadPaymentAdjustmentAttachments = (row: PaymentJobRecipientResponse) => {
    if (!effectiveJobId) return
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    input.onchange = async () => {
      const files = Array.from(input.files ?? [])
      if (files.length === 0) return
      setAttachmentProcessingLineId(row.id)
      try {
        await paymentJobsApi.uploadPaymentAdjustmentAttachments(effectiveJobId, row.id, files)
        toast.success('添付ファイルをアップロードしました。')
        await fetchPaymentAdjustmentAttachments(row, false)
      } catch (error) {
        if (!isHandledAuthError(error)) {
          Modal.error({ title: 'システムエラー', content: '添付ファイルをアップロードできませんでした。' })
        }
      } finally {
        setAttachmentProcessingLineId(null)
      }
    }
    input.click()
  }

  const openPaymentAdjustmentAttachmentList = (row: PaymentJobRecipientResponse) => {
    void fetchPaymentAdjustmentAttachments(row, true)
  }

  const downloadPaymentAdjustmentAttachments = async (row: PaymentJobRecipientResponse) => {
    if (!effectiveJobId) return
    setAttachmentProcessingLineId(row.id)
    try {
      const response = await paymentJobsApi.downloadPaymentAdjustmentAttachments(effectiveJobId, row.id)
      downloadBlob(response.data, `振込金額修正添付ファイル_${row.identId}.zip`)
    } catch (error) {
      if (!isHandledAuthError(error)) {
        Modal.error({ title: 'システムエラー', content: '添付ファイルをダウンロードできませんでした。' })
      }
    } finally {
      setAttachmentProcessingLineId(null)
    }
  }

  const deletePaymentAdjustmentAttachment = (row: PaymentJobRecipientResponse, file: PaymentAdjustmentAttachmentInfo) => {
    if (!effectiveJobId) return
    Modal.confirm({
      title: '添付ファイルを削除しますか？',
      content: <span className="break-all">{file.filename}</span>,
      okText: '削除',
      okButtonProps: { danger: true },
      cancelText: 'キャンセル',
      onOk: async () => {
        setAttachmentProcessingLineId(row.id)
        try {
          await paymentJobsApi.deletePaymentAdjustmentAttachment(effectiveJobId, row.id, file.filename)
          toast.success('添付ファイルを削除しました。')
          await fetchPaymentAdjustmentAttachments(row, false)
        } catch (error) {
          if (!isHandledAuthError(error)) {
            Modal.error({ title: 'システムエラー', content: '添付ファイルを削除できませんでした。' })
          }
        } finally {
          setAttachmentProcessingLineId(null)
        }
      },
    })
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: '支給者管理', page: 'payment-job' }, { label: '支給者一覧' }]} />
      <div className="mx-auto max-w-7xl px-6 py-8">
        <BackButton onClick={() => navigate('payment-job')} />

        {job && (
          <div className="mb-4 rounded-xl bg-white p-4 text-sm text-gray-700 shadow-lg">
            <div>
              <span className="font-mono font-medium">{job.jobNo}</span>
              <span className="mx-2 text-gray-300">|</span>
              <span>
                振込指定日: <span className="font-medium">{job.transferDate || EMPTY_DISPLAY}</span>
              </span>
              <span className="mx-2 text-gray-300">|</span>
              <span>{job.jobName}</span>
            </div>
          </div>
        )}

        <div className="mb-6 rounded-xl bg-white p-6 shadow-lg">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xl font-bold text-gray-800">検索条件</h2>
            <span className="shrink-0 text-xs text-gray-500">AND検索</span>
          </div>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <input
              type="text"
              value={qIdentId}
              onChange={(event) => setQIdentId(event.target.value)}
              placeholder="識別ID"
              className="rounded-lg border border-gray-300 px-4 py-2"
            />
            <input
              type="text"
              value={qName}
              onChange={(event) => setQName(event.target.value)}
              placeholder="氏名"
              className="rounded-lg border border-gray-300 px-4 py-2"
            />
            <input
              type="text"
              value={qHouseholdNo}
              onChange={(event) => setQHouseholdNo(event.target.value)}
              placeholder="世帯番号"
              className="rounded-lg border border-gray-300 px-4 py-2"
            />
            <select
              aria-label="事務局確認用ステータス"
              value={qOfficeReview}
              onChange={(event) => setQOfficeReview(event.target.value)}
              className="rounded-lg border border-gray-300 px-4 py-2"
            >
              <option value={OFFICE_REVIEW_ALL_VALUE}>事務局確認用ステータス（全て）</option>
              <option value={OFFICE_REVIEW_UNSET_VALUE}>事務局確認用ステータス：未設定</option>
              {officeReviewStatuses.map((status) => (
                <option key={status.id} value={String(status.id)}>
                  {status.displayName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end">
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={runClear}
                className="cursor-pointer rounded-lg border border-gray-300 px-6 py-2 transition hover:bg-gray-50"
              >
                クリア
              </button>
              <button
                type="button"
                onClick={runSearch}
                className="cursor-pointer rounded-lg bg-blue-600 px-6 py-2 text-white shadow-md transition hover:bg-blue-700"
              >
                検索
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-lg">
          <h2 className="mb-4 text-xl font-bold text-gray-800">検索結果（{filteredRows.length}件）</h2>
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[12%]" />
                <col className="w-[9%]" />
                <col className="w-[11%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
                <col className="w-[8%]" />
                <col className="w-[13%]" />
                <col className="w-[12%]" />
                <col className="w-[18%]" />
              </colgroup>
              <thead className="border-b-2 border-gray-200 bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left text-sm font-medium text-gray-700">識別ID</th>
                  <th className="px-2 py-2 text-left text-sm font-medium text-gray-700">世帯番号</th>
                  <th className="px-2 py-2 text-left text-sm font-medium text-gray-700">氏名</th>
                  <th className="px-2 py-2 text-right text-sm font-medium text-gray-700">計算金額</th>
                  <th className="px-2 py-2 text-right text-sm font-medium text-gray-700">調整額</th>
                  <th className="px-2 py-2 text-right text-sm font-medium text-gray-700">振込金額</th>
                  <th className="px-2 py-2 text-left text-sm font-medium text-gray-700">ステータス</th>
                  <th className="px-2 py-2 text-left text-sm font-medium leading-tight text-gray-700">事務局確認用ステータス</th>
                  <th className="px-2 py-2 text-left text-sm font-medium text-gray-700">操作</th>
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-200 transition-opacity ${loading ? 'opacity-60' : ''}`}>
                {filteredRows.length === 0 ? (
                  loading ? (
                    <TableLoadingRow colSpan={9} />
                  ) : (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500">
                        該当するデータがありません
                      </td>
                    </tr>
                  )
                ) : (
                  filteredRows.map((row) => (
                    <tr key={row.id} className="transition hover:bg-blue-50">
                      <td className="px-2 py-2 text-sm font-mono">
                        {row.identId ? (
                          <button
                            type="button"
                            title={row.identId}
                            onClick={() =>
                              navigate('recipient-detail', {
                                identId: row.identId,
                                detailPageTitle: '受給者情報詳細',
                                baseCrumb: { label: '支給者一覧', page: 'payment-job-detail', navigateOpts: { paymentJobId: effectiveJobId ?? undefined } },
                              })
                            }
                            className="block max-w-full cursor-pointer truncate text-left text-blue-600 hover:underline"
                          >
                            {row.identId}
                          </button>
                        ) : (
                          EMPTY_DISPLAY
                        )}
                      </td>
                      <td className="truncate px-2 py-2 text-sm font-mono" title={row.householdNo || undefined}>{row.householdNo || EMPTY_DISPLAY}</td>
                      <td className="truncate px-2 py-2 text-sm" title={row.name || undefined}>{row.name || EMPTY_DISPLAY}</td>
                      <td className="px-2 py-2 text-right text-sm">
                        {(() => {
                          const code = row.caseStatusCode as CaseStatus | null | undefined
                          const calculated =
                            code != null && CASE_STATUS_CODE_MAP[code] != null
                              ? CASE_STATUS_CODE_MAP[code] >= CASE_STATUS_CODE_MAP.CALC_DONE
                              : false
                          if (!calculated) return EMPTY_DISPLAY
                          return Number(row.calculatedAmount ?? row.amount).toLocaleString()
                        })()}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right text-sm">
                        <span className={Number(row.adjustmentAmount ?? 0) < 0 ? 'text-red-600' : Number(row.adjustmentAmount ?? 0) > 0 ? 'text-blue-600' : ''}>
                          {formatSignedYen(row.adjustmentAmount ?? 0).replace('円', '')}
                        </span>
                        {row.adjustedAt ? (
                          <button
                            type="button"
                            aria-label="振込金額修正内容を表示"
                            title="振込金額修正内容を表示"
                            onClick={() => setDetailRow(row)}
                            className="ml-2 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-blue-400 text-xs font-bold text-blue-600 hover:bg-blue-50"
                          >
                            i
                          </button>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right text-sm font-semibold">{formatYen(row.amount).replace('円', '')}</td>
                      <td className="px-2 py-2 text-sm">
                        {row.caseStatusLabel ? <DemoBadge status={row.caseStatusLabel} /> : EMPTY_DISPLAY}
                      </td>
                      <td className="truncate px-2 py-2 text-sm">
                        <OfficeReviewStatusTag statusId={row.officeReviewStatusId} />
                      </td>
                      <td className="px-2 py-2 text-sm">
                        <div className="flex flex-col items-start gap-1">
                          {canEditPayment && ADJUSTABLE_CASE_STATUSES.has(row.caseStatusCode ?? '') ? (
                            <button
                              type="button"
                              onClick={() => openAdjustment(row)}
                              className="cursor-pointer whitespace-nowrap text-blue-600 hover:underline"
                            >
                              振込金額修正
                            </button>
                          ) : row.caseStatusCode === 'ZENGIN_PROD_EXPORTED' ? (
                            <button
                              type="button"
                              disabled
                              title="振込データ出力後のため修正できません"
                              className="cursor-not-allowed whitespace-nowrap text-gray-400"
                            >
                              修正不可
                            </button>
                          ) : null}
                          {row.adjustedAt && canManageAdjustmentAttachments ? (
                            <button
                              type="button"
                              disabled={attachmentProcessingLineId === row.id}
                              onClick={() => openPaymentAdjustmentAttachmentList(row)}
                              className="cursor-pointer whitespace-nowrap text-slate-700 hover:underline disabled:cursor-wait disabled:text-gray-400"
                            >
                              添付ファイル管理
                            </button>
                          ) : null}
                          {!((canEditPayment && ADJUSTABLE_CASE_STATUSES.has(row.caseStatusCode ?? ''))
                            || row.caseStatusCode === 'ZENGIN_PROD_EXPORTED'
                            || (row.adjustedAt && canManageAdjustmentAttachments))
                            ? EMPTY_DISPLAY
                            : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal
        title="振込金額修正"
        open={editingRow != null}
        onCancel={closeAdjustment}
        onOk={() => void saveAdjustment()}
        okText="更新"
        cancelText="キャンセル"
        confirmLoading={savingAdjustment}
        maskClosable={!savingAdjustment}
        destroyOnHidden
      >
        {editingRow ? (
          <div className="space-y-5 pt-2">
            <div className="grid grid-cols-[100px_1fr] gap-x-3 gap-y-2 rounded-lg bg-gray-50 p-3 text-sm">
              <span className="text-gray-500">識別ID</span><strong>{editingRow.identId}</strong>
              <span className="text-gray-500">氏名</span><strong>{editingRow.name}</strong>
              <span className="text-gray-500">ステータス</span><strong>{editingRow.caseStatusLabel}</strong>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">調整額 <span className="text-red-600">必須</span></label>
              <InputNumber
                value={adjustmentAmount}
                onChange={(value) => setAdjustmentAmount(value)}
                precision={0}
                step={1}
                min={-9_999_999_999}
                max={9_999_999_999}
                className="w-full"
                addonAfter="円"
              />
              <p className="mt-1 text-xs text-gray-500">減額はマイナス、増額はプラスで入力します。</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">修正理由 <span className="text-red-600">必須</span></label>
              <Input.TextArea
                value={adjustmentReason}
                onChange={(event) => setAdjustmentReason(event.target.value)}
                maxLength={128}
                showCount
                rows={3}
              />
            </div>
            <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-3 rounded-lg bg-blue-50 p-4 text-center">
              <div><div className="text-xs text-gray-500">計算金額</div><strong>{formatYen(editingCalculatedAmount)}</strong></div>
              <span>＋</span>
              <div><div className="text-xs text-gray-500">調整額</div><strong>{formatSignedYen(adjustmentAmount)}</strong></div>
              <span>＝</span>
              <div><div className="text-xs text-gray-500">振込金額</div><strong className={editingFinalAmount < 0 ? 'text-red-600' : ''}>{formatYen(editingFinalAmount)}</strong></div>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        title="振込金額修正内容"
        open={detailRow != null}
        onCancel={() => setDetailRow(null)}
        onOk={() => setDetailRow(null)}
        okText="閉じる"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {detailRow ? (
          <div className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-3 pt-3 text-sm">
            <span className="text-gray-500">識別ID</span><strong>{detailRow.identId}</strong>
            <span className="text-gray-500">修正理由</span><span className="break-words">{detailRow.adjustmentReason || EMPTY_DISPLAY}</span>
            <span className="text-gray-500">修正者</span><span>{detailRow.adjustedByName || EMPTY_DISPLAY}</span>
            <span className="text-gray-500">修正日時</span><span>{formatAdjustedAt(detailRow.adjustedAt)}</span>
            <span className="text-gray-500">修正前金額</span><span>{formatYen(detailRow.adjustmentPreviousAmount)}</span>
            <span className="text-gray-500">調整額</span><span>{formatSignedYen(detailRow.adjustmentAmount)}</span>
            <span className="text-gray-500">修正後金額</span><strong>{formatYen(detailRow.amount)}</strong>
          </div>
        ) : null}
      </Modal>

      <Modal
        title={`添付ファイル管理${attachmentListRow && !attachmentListLoading ? `（${attachmentFiles.length}件）` : ''}${attachmentListRow ? ` - ${attachmentListRow.identId}` : ''}`}
        open={attachmentListRow != null}
        onCancel={() => {
          if (!attachmentListLoading && attachmentProcessingLineId == null) setAttachmentListRow(null)
        }}
        onOk={() => setAttachmentListRow(null)}
        okText="閉じる"
        okButtonProps={{ disabled: attachmentListLoading || attachmentProcessingLineId != null }}
        cancelButtonProps={{ style: { display: 'none' } }}
        confirmLoading={attachmentListLoading}
        width={720}
      >
        <div className="pt-3">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <button
              type="button"
              disabled={attachmentListRow == null || attachmentListLoading || attachmentProcessingLineId != null}
              onClick={() => {
                if (attachmentListRow) uploadPaymentAdjustmentAttachments(attachmentListRow)
              }}
              className="cursor-pointer rounded-md bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-wait disabled:bg-gray-300"
            >
              添付ファイルアップロード
            </button>
            <button
              type="button"
              disabled={attachmentListRow == null || attachmentListLoading || attachmentProcessingLineId != null || attachmentFiles.length === 0}
              onClick={() => {
                if (attachmentListRow) void downloadPaymentAdjustmentAttachments(attachmentListRow)
              }}
              title={!attachmentListLoading && attachmentFiles.length === 0 ? '添付ファイルが登録されていません' : undefined}
              className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              一括ダウンロード（ZIP）
            </button>
          </div>
          {attachmentListLoading ? (
            <div className="py-10 text-center text-sm text-gray-500">読み込み中...</div>
          ) : attachmentFiles.length === 0 ? (
            <div className="rounded-lg bg-gray-50 py-10 text-center text-sm text-gray-500">
              添付ファイルは登録されていません
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-[38%] px-4 py-3 text-left text-sm font-medium text-gray-700">ファイル名</th>
                    <th className="w-[28%] px-4 py-3 text-left text-sm font-medium text-gray-700">最終アップロード日時</th>
                    <th className="w-[14%] px-4 py-3 text-right text-sm font-medium text-gray-700">サイズ</th>
                    <th className="w-[20%] px-4 py-3 text-center text-sm font-medium text-gray-700">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {attachmentFiles.map((file) => (
                    <tr key={file.filename}>
                      <td className="break-all px-4 py-3 text-sm text-gray-900">{file.filename}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        {formatAttachmentDateTime(file.lastModified)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                        {formatFileSize(file.size)}
                      </td>
                      <td className="px-4 py-3 text-center text-sm">
                        <button
                          type="button"
                          disabled={attachmentListRow == null || attachmentProcessingLineId === attachmentListRow.id}
                          onClick={() => {
                            if (attachmentListRow) deletePaymentAdjustmentAttachment(attachmentListRow, file)
                          }}
                          className="cursor-pointer rounded px-2 py-1 font-medium text-red-600 hover:bg-red-50 hover:text-red-700 disabled:cursor-wait disabled:text-gray-400"
                        >
                          削除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default PaymentJobDetail
